import React, { FC, useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isMobile } from 'react-device-detect';

import {
  Button,
  StaleBtnGroup,
  StaleInput,
  StaleInputCurrency,
  StaleTitleH1,
  StaleTitleH5,
  ButtonStyleLink,
  StaleSelectMenu,
  StaleOverflowScroll,
} from 'components';
import { SignUpContent } from '..';
import { ICountry, TBusinessNatureUnion } from 'types';
import { useStoreActions, useStoreState } from 'state';
import { useHistory } from 'react-router-dom';
import orderBy from 'lodash.orderby';
import { ERROR_MESSAGES } from 'variables';

const MONTHLY_TURNOVER_OPTIONS = [
  {
    value: {
      expectedFxTurnoverFrom: null,
      expectedFxTurnoverUpperLimit: 10000,
    },
    label: 'Less than GBP 10,000',
  },
  {
    value: {
      expectedFxTurnoverFrom: 10001,
      expectedFxTurnoverUpperLimit: 25000,
    },
    label: 'GBP 10,001 - 25,000',
  },
  {
    value: {
      expectedFxTurnoverFrom: 25001,
      expectedFxTurnoverUpperLimit: 50000,
    },
    label: 'GBP 25,001 - 50,000',
  },
  {
    value: {
      expectedFxTurnoverFrom: 50001,
      expectedFxTurnoverUpperLimit: 100000,
    },
    label: 'GBP 50,001 - 100,000',
  },
  {
    value: {
      expectedFxTurnoverFrom: 100001,
      expectedFxTurnoverUpperLimit: 200000,
    },
    label: 'More than GBP 100,000',
  },
];

type Inputs = {
  businessNature: TBusinessNatureUnion;
  expectedAnnualTurnover: string;
  // TODO: correct type
  expectedForeignCurrencyTurnover: string;
  countries: ICountry[];
  accountPurpose: string;
  otherNatureOfBusinessLabel?: string;
};

interface OwnProps {
  onSaveValues: (values: any) => void;
  onContinue: () => void;
  savedValues: any;
}

const StepOne: FC<OwnProps> = ({ onSaveValues, onContinue, savedValues }) => {
  const history = useHistory();

  const [businessSearchValue, setBusinessSearchValue] = useState('');
  const [countriesSearchValue, setCountriesSearchValue] = useState('');
  const { countries, naturesOfBusiness } = useStoreState(
    (state) => state.ReferenceDataState
  );
  const { getCountries, getNaturesOfBusiness } = useStoreActions(
    (actions) => actions.ReferenceDataState
  );

  const parsedCountries = useMemo(
    () =>
      orderBy(
        Object.values(countries).map((item) => ({
          value: item,
          label: item.name,
          icon: item.alpha2,
        })),
        'label'
      ),
    [countries]
  );

  useEffect(() => {
    getCountries();
    getNaturesOfBusiness();
  }, [getCountries, getNaturesOfBusiness]);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
  } = useForm<Inputs>({
    mode: 'onChange',
  });

  const businessNatureWatch = watch('businessNature');
  const isBusinessNatureOther = businessNatureWatch?.code === 'other';

  const onSubmit = (values) => {
    onSaveValues(values);
    onContinue();
  };

  const onError = (errors) => {
    // TODO: discuss how to handle errors
  };

  return (
    <>
      <SignUpContent>
        {!isMobile && (
          <ButtonStyleLink
            className="cross"
            onClick={() => history.push('/app/dashboard')}
          >
            <svg width="24" height="24">
              <use xlinkHref="#cross-ico" />
            </svg>
          </ButtonStyleLink>
        )}

        <StaleOverflowScroll>
          <div className="block">
            {isMobile ? (
              <StaleTitleH5>Please fill your business info</StaleTitleH5>
            ) : (
              <StaleTitleH1>Please fill your company information </StaleTitleH1>
            )}

            <form
              id="company-registration-step-one-form"
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              <div className="field">
                <Controller
                  control={control}
                  name="businessNature"
                  defaultValue={savedValues.businessNature}
                  rules={{ required: ERROR_MESSAGES.requiredField }}
                  render={({ onChange, value, name }) => {
                    return (
                      <StaleSelectMenu
                        id="business nature"
                        name={name}
                        label="Nature of business"
                        isMulti={false}
                        data={naturesOfBusiness}
                        value={value}
                        onChange={onChange}
                        // TODO: add debounce for search
                        inputValue={businessSearchValue}
                        onInputChange={setBusinessSearchValue}
                        autoFocus
                      />
                    );
                  }}
                />
              </div>

              {isBusinessNatureOther && (
                <div className="field">
                  <StaleInput
                    id="otherNatureOfBusinessLabel"
                    label="Your nature of business"
                    view="moving"
                    type="text"
                    name="otherNatureOfBusinessLabel"
                    defaultValue={savedValues.otherNatureOfBusinessLabel}
                    control={control}
                    rules={{
                      required: ERROR_MESSAGES.requiredField,
                    }}
                  />
                </div>
              )}

              <div className="field">
                <StaleInputCurrency
                  id="expected-annual-turnover"
                  label="Expected Annual Turnover"
                  view="moving"
                  type="text"
                  name="expectedAnnualTurnover"
                  rules={{
                    required: ERROR_MESSAGES.requiredField,
                  }}
                  control={control}
                  defaultValue={savedValues.expectedAnnualTurnover}
                  currencyPrefix="GBP"
                />
              </div>

              <div className="field">
                <Controller
                  control={control}
                  name="expectedMonthlyForeignCurrencyTurnover"
                  defaultValue={
                    savedValues.expectedMonthlyForeignCurrencyTurnover
                  }
                  rules={{
                    required: ERROR_MESSAGES.requiredField,
                  }}
                  render={({ onChange, value, name }) => {
                    return (
                      <StaleSelectMenu
                        id="expected-foreign-currency-turnover"
                        label="Expected Monthly Foreign Currency Turnover"
                        name={name}
                        isMulti={false}
                        data={MONTHLY_TURNOVER_OPTIONS}
                        value={value}
                        onChange={onChange}
                      />
                    );
                  }}
                />
              </div>

              <div className="field">
                <Controller
                  control={control}
                  name="countries"
                  defaultValue={savedValues.countries}
                  rules={{
                    required: ERROR_MESSAGES.requiredField,
                  }}
                  render={({ onChange, value, name }) => {
                    return (
                      <StaleSelectMenu
                        id="countries"
                        label="Countries you'll be sending money to"
                        name={name}
                        isMulti={true}
                        data={parsedCountries}
                        value={value}
                        onChange={(value) => {
                          if (!value.length) {
                            onChange(null);
                          } else {
                            onChange(value);
                          }
                        }}
                        // TODO: add debounce for search
                        inputValue={countriesSearchValue}
                        onInputChange={setCountriesSearchValue}
                      />
                    );
                  }}
                />
              </div>

              <div className="field">
                <StaleInput
                  id="account-purpose"
                  label="Purpose of Account"
                  view="moving"
                  type="text"
                  name="accountPurpose"
                  defaultValue={savedValues.accountPurpose}
                  control={control}
                  rules={{
                    required: ERROR_MESSAGES.requiredField,
                  }}
                />
              </div>
            </form>

            {!isMobile && (
              <StaleBtnGroup>
                <Button
                  disabled={!isValid}
                  form="company-registration-step-one-form"
                >
                  Continue
                </Button>
              </StaleBtnGroup>
            )}
          </div>
        </StaleOverflowScroll>
      </SignUpContent>

      {isMobile && (
        <StaleBtnGroup>
          <Button disabled={!isValid} form="company-registration-step-one-form">
            Continue
          </Button>
        </StaleBtnGroup>
      )}
    </>
  );
};

export default StepOne;
